import { isMobile } from '../core/device';

const T_COLOR = '#e8b849';
const CT_COLOR = '#5b9bd5';

export type RoundPhase = 'warmup' | 'freeze_time' | 'active' | 'round_end';
export type RoundContextType = 'regular' | 'overtime' | 'match-point';

export interface RoundContext {
  type: RoundContextType;
  round: number;
}

const PHASE_LABELS: Record<RoundPhase, string> = {
  warmup: 'WARMUP',
  freeze_time: 'FREEZE TIME',
  active: '',
  round_end: 'ROUND OVER',
};

export class RoundScore {
  private container: HTMLElement;
  private scoreEl: HTMLElement;
  private contextEl: HTMLElement;
  private roundEl: HTMLElement;
  private phaseEl: HTMLElement;
  private bannerEl: HTMLElement;
  private freezeOverlay: HTMLElement;
  private bannerTimer: ReturnType<typeof setTimeout> | null = null;
  private currentPhase: RoundPhase = 'active';

  constructor() {
    const mobile = isMobile();

    // Main container
    this.container = document.createElement('div');
    Object.assign(this.container.style, {
      position: 'fixed',
      top: '40px',
      left: '50%',
      transform: 'translateX(-50%)',
      textAlign: 'center',
      zIndex: '25',
      pointerEvents: 'none',
      fontFamily: 'Arial, sans-serif',
      userSelect: 'none',
    });

    // Score line: T [score] : [score] CT
    const scoreRow = document.createElement('div');
    Object.assign(scoreRow.style, {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
    });

    this.scoreEl = document.createElement('div');
    Object.assign(this.scoreEl.style, {
      fontSize: '22px',
      fontWeight: 'bold',
      letterSpacing: '2px',
      color: '#ffffff',
      textShadow: '0 1px 4px rgba(0,0,0,0.8)',
    });
    scoreRow.appendChild(this.scoreEl);

    this.contextEl = document.createElement('div');
    Object.assign(this.contextEl.style, {
      display: 'none',
      padding: '2px 6px',
      border: '1px solid rgba(255,255,255,0.22)',
      borderRadius: '3px',
      background: 'rgba(0,0,0,0.42)',
      color: '#dcdcdc',
      fontSize: '10px',
      fontWeight: 'bold',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      textShadow: '0 1px 3px rgba(0,0,0,0.8)',
      whiteSpace: 'nowrap',
    });
    scoreRow.appendChild(this.contextEl);
    this.container.appendChild(scoreRow);

    // Round info
    this.roundEl = document.createElement('div');
    Object.assign(this.roundEl.style, {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#ffffff',
      marginTop: '4px',
      letterSpacing: '2px',
      textShadow: '0 1px 5px rgba(0,0,0,0.9)',
    });
    this.container.appendChild(this.roundEl);

    // Phase label (warmup, freeze time, etc.)
    this.phaseEl = document.createElement('div');
    Object.assign(this.phaseEl.style, {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#ffcc00',
      marginTop: '4px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      display: 'none',
    });
    this.container.appendChild(this.phaseEl);

    // Round-end banner (hidden by default)
    this.bannerEl = document.createElement('div');
    Object.assign(this.bannerEl.style, {
      position: 'fixed',
      top: '35%',
      left: '0',
      width: '100%',
      minHeight: '60px',
      height: 'auto',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.75)',
      zIndex: '50',
      pointerEvents: 'none',
      fontFamily: 'Arial, sans-serif',
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#ffffff',
      letterSpacing: '3px',
      textTransform: 'uppercase',
      textShadow: '0 2px 8px rgba(0,0,0,0.6)',
      whiteSpace: 'normal',
    });

    // Freeze time overlay
    this.freezeOverlay = document.createElement('div');
    Object.assign(this.freezeOverlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: 'rgba(0, 100, 200, 0.08)',
      border: mobile ? 'none' : '3px solid rgba(80, 160, 255, 0.25)',
      zIndex: '18',
      pointerEvents: 'none',
      display: 'none',
    });

    document.body.appendChild(this.container);
    document.body.appendChild(this.bannerEl);
    document.body.appendChild(this.freezeOverlay);
  }

  update(tScore: number, ctScore: number, round: number, maxRounds: number): void {
    this.scoreEl.innerHTML =
      `<span style="color:${T_COLOR}">T</span>` +
      ` <span style="font-size:28px;">${tScore}</span>` +
      ` <span style="color:#666;margin:0 6px;">:</span> ` +
      `<span style="font-size:28px;">${ctScore}</span>` +
      ` <span style="color:${CT_COLOR}">CT</span>`;

    this.roundEl.textContent = maxRounds > 0
      ? `ROUND ${round} / ${maxRounds}`
      : `ROUND ${round}`;
  }

  setRoundContext(context: RoundContext): void {
    const label =
      context.type === 'regular'
        ? 'MR12'
        : context.type === 'overtime'
          ? 'OT'
          : 'MATCH POINT';
    this.contextEl.textContent = `${label} R${context.round}`;
    this.contextEl.style.display = 'block';
    this.contextEl.style.color = context.type === 'match-point' ? '#ffcc66' : '#dcdcdc';
    this.contextEl.style.borderColor =
      context.type === 'match-point' ? 'rgba(255,204,102,0.55)' : 'rgba(255,255,255,0.22)';
  }

  setPhase(phase: RoundPhase): void {
    this.currentPhase = phase;
    const label = PHASE_LABELS[phase];
    if (label) {
      this.phaseEl.textContent = label;
      this.phaseEl.style.display = 'block';
      // Color coding per phase
      if (phase === 'freeze_time') {
        this.phaseEl.style.color = '#66bbff';
      } else if (phase === 'warmup') {
        this.phaseEl.style.color = '#ffcc00';
      } else if (phase === 'round_end') {
        this.phaseEl.style.color = '#ff6644';
      }
    } else {
      this.phaseEl.style.display = 'none';
    }

    // Freeze time overlay
    this.freezeOverlay.style.display = phase === 'freeze_time' ? 'block' : 'none';
  }

  getPhase(): RoundPhase {
    return this.currentPhase;
  }

  showRoundEnd(winner: string, reason: string): void {
    const winColor = winner.toLowerCase() === 't' ? T_COLOR : CT_COLOR;
    this.bannerEl.innerHTML =
      `<span style="color:${winColor}">${winner.toUpperCase()}</span>` +
      `<span style="margin:0 12px;color:#888;">—</span>` +
      `<span>${reason}</span>`;
    this.bannerEl.style.display = 'flex';

    if (this.bannerTimer) clearTimeout(this.bannerTimer);
    this.bannerTimer = setTimeout(() => {
      this.bannerEl.style.display = 'none';
    }, 3000);
  }

  show(): void {
    this.container.style.display = 'block';
  }

  hide(): void {
    this.container.style.display = 'none';
    this.bannerEl.style.display = 'none';
    this.freezeOverlay.style.display = 'none';
  }

  dispose(): void {
    if (this.bannerTimer) clearTimeout(this.bannerTimer);
    this.container.remove();
    this.bannerEl.remove();
    this.freezeOverlay.remove();
  }
}
